import { initializeApp } from 'firebase/app';

import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signOut,
  signInWithRedirect,
  getRedirectResult,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';


const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

if (!firebaseConfig.apiKey || !firebaseConfig.authDomain || !firebaseConfig.projectId || !firebaseConfig.appId) {
  throw new Error('Firebase configuration is incomplete. Check your .env / environment variables for REACT_APP_FIREBASE_* values.');
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
//const analytics = getAnalytics(app);

// Initialize Firebase Authentication
const auth = getAuth(app);

setPersistence(auth, browserLocalPersistence)
  .then(() => {
    console.log('Persistence set to LOCAL');
  })
  .catch((error) => {
    console.error('Error setting persistence:', error);
  });

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();

const signUpWithEmailPassword = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential.user; 
  } catch (error) {
    throw error; 
  }
};

// Sign in with email and password
const signInWithEmailPassword = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user; 
  } catch (error) {
    throw error; 
  }
};

const signInWithGoogle = async () => {
  try {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Force the Google account picker on every attempt. Without this, Google
    // silently uses the account already signed into the browser and the user
    // never gets to choose which account to sign up/in with.
    googleProvider.setCustomParameters({ prompt: 'select_account' });

    // Always use a popup first to ensure Firebase can set cookies
    try {
      const result = await signInWithPopup(auth, googleProvider);
      console.log("Popup signin successful");
      return result.user;
    } catch (popupError) {
      console.log("Popup failed, error:", popupError.code);

      // If error is related to popup being blocked or we're on mobile
      if (popupError.code === 'auth/popup-blocked' ||
          popupError.code === 'auth/popup-closed-by-user' ||
          isMobile) {
        console.log("Falling back to redirect");
        await signInWithRedirect(auth, googleProvider);
        // This line won't execute on redirect
      } else {
        // Some other error
        throw popupError;
      }
    }
  } catch (error) {
    console.error("Google signin error:", error);
    throw error;
  }
};

// Sign out
const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    throw error; 
  }
};

const deleteUser = async (user) => {
  try {
    await user.delete();
  } catch (error) {
    throw error; 
  }
};

const sendPassResetEmail = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw error; 
  }
};

const checkRedirectResult = async () => {
  try {
    // Try to access localStorage as a test
    const testKey = `firebase:authUser:${firebaseConfig.apiKey}:[DEFAULT]`;
    const testAccess = localStorage.getItem(testKey) !== null;
    
    console.log("Storage access check:", testAccess ? "Available" : "Restricted");
    
    // Now check redirect result
    const result = await getRedirectResult(auth);
    console.log('Redirect result:', result);
    return result;
  } catch (error) {
    console.error('Error in redirectResult:', error);
    // This could indicate storage access is blocked
    return null;
  }
};

const redirectResult = async () => {
  return checkRedirectResult();
};

// Export all authentication methods
export {
  auth,
  signUpWithEmailPassword,
  signInWithEmailPassword,
  signInWithGoogle,
  signOutUser,
  deleteUser,
  sendPassResetEmail,
  redirectResult,
};
